import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';
import EmptyState from './EmptyState';
import NoSessionsIllustration from './illustrations/NoSessionsIllustration';

interface Props {
  children: ReactNode;
}

// Gates project-scoped pages (Dashboard, Generator, API Testing, Web Automation) behind an
// active project — project selection is mandatory everywhere a session gets created, per
// .claude/rules/15-project-git-config-mandatory-selection.md. Settings routes are never
// wrapped in this, so a first-time org can always reach Settings -> Projects to create one.
export default function RequireProject({ children }: Props) {
  const { loading, activeProjectId, projects } = useProject();
  const activeProjects = projects.filter(p => p.status === 'active');

  if (loading) return null;

  if (activeProjects.length === 0) {
    return (
      <EmptyState illustration={<NoSessionsIllustration />} title="No projects yet">
        Create a project under Settings to start generating and reviewing test cases —{' '}
        <Link to="/settings/projects">go to Settings &rarr; Projects</Link>.
      </EmptyState>
    );
  }

  if (!activeProjectId) {
    return (
      <EmptyState illustration={<NoSessionsIllustration />} title="Select a project">
        Choose a project from the sidebar to continue.
      </EmptyState>
    );
  }

  return <>{children}</>;
}
